import qs from 'qs';

import url from 'url';

import { getI18nLocale, translate } from '@front/i18n';

import { isSuccessful } from '../utils/http-status';
import { getCurrentCountry } from '../utils/country';

import { JsonResponse, RequestOptions } from './types';
import ApiError from './api-error';

type Headers = Record<string, string>;

let baseUrl: string;
let headers: Headers;
let authorizationToken: string | null;

const t = translate('alerts');

export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

/*
 * errors.payment and errors.potential are specific case for createPotential API
 */
const errorMessage = (body: JsonResponse): string => {
  if (body.message) {
    return body.message;
  }
  if (body.errors) {
    if (body.errors instanceof Array) {
      return body.errors.join('. ');
    }
    if (body.errors.payment) {
      return body.errors.payment;
    }
    if (body.errors.potential) {
      return body.errors.potential.join('. ');
    }
    return String(body.errors);
  }
  if (body.error) {
    return body.error;
  }
  return t('.unknown_api_error.message_with_detail', { detail: JSON.stringify(body) });
};

// We export this function to test that it works as expected
export const formatUrl = (mainUrl: string, pathname: string, options?: RequestOptions) => {
  const baseConfig = url.parse(mainUrl);
  return url.format({
    auth: baseConfig.auth,
    hostname: baseConfig.hostname,
    port: baseConfig.port,
    protocol: baseConfig.protocol,
    pathname: baseConfig.pathname + pathname,
    search:
      options?.search ||
      qs.stringify(options?.query, {
        arrayFormat: 'brackets',
      }),
  });
};

const buildHeaders = (isFormData: boolean): Headers => {
  const baseHeaders = {
    ...headers,
    'Accept-Language': getI18nLocale(),
    'Wecasa-Country': getCurrentCountry(),
    'Content-Type': isFormData ? 'multipart/form-data' : 'application/json',
  };
  return authorizationToken ? { ...baseHeaders, authorization: authorizationToken } : baseHeaders;
};

const getBody = (options: RequestOptions) =>
  options.isFormData ? (options.body as FormData) : JSON.stringify(options.body);

const makeRequest = async (method: string, requestUrl: string, options: RequestOptions = {}) => {
  try {
    const path = formatUrl(options.baseUrl || baseUrl, requestUrl, options);
    const response = await fetch(path, {
      method,
      headers: buildHeaders(options.isFormData || false),
      body: getBody(options),
    });
    if (isSuccessful(response.status)) {
      if (options.headersOnly) return response.headers;

      // awaiting for json function is required here to catch json parsing errors
      return options.skipJsonParsing ? response : await response.json();
    }
    const body = await response.json();
    const message = errorMessage(body);
    throw new ApiError(
      message,
      response.status,
      body.errors instanceof Array || typeof body.errors === 'object' ? body.errors : [message],
      body,
    );
  } catch (error) {
    // For standard errors like parse errors we add the errors attribute so it is always present
    if (error instanceof Error && !(error as any).errors) {
      if (error.message.includes('Network request failed')) {
        error.message = t('.network_error.message');
      } else if (
        error.message.includes('Unexpected end of JSON input') ||
        error.message.includes('Unexpected token')
      ) {
        error.message = t('.unknown_api_error.message');
      }
      throw Object.assign(error, { errors: [error.message] });
    }
    throw error;
  }
};

export const init = (baseUrlValue: string, userAgent?: string) => {
  baseUrl = baseUrlValue;
  headers = userAgent
    ? {
        ...DEFAULT_HEADERS,
        'User-Agent': userAgent,
      }
    : {
        ...DEFAULT_HEADERS,
      };
};

export const setAuthorizationToken = (token: string | null) => {
  authorizationToken = token;
};

export const getAuthorizationToken = () => authorizationToken;

export const get = (requestUrl: string, options: RequestOptions = {}) => makeRequest('GET', requestUrl, options);

export const post = (requestUrl: string, options: RequestOptions = {}) => makeRequest('POST', requestUrl, options);

export const put = (requestUrl: string, options: RequestOptions = {}) => makeRequest('PUT', requestUrl, options);

export const del = (requestUrl: string, options: RequestOptions = {}) => makeRequest('DELETE', requestUrl, options);

export const fetchWithTimeout = async (requestUrl: RequestInfo, options: RequestInit & { timeout?: number } = {}) => {
  const { timeout = 8000, ...requestOptions } = options;

  const controller = new AbortController();
  const id = setTimeout(() => {
    controller.abort();
  }, timeout);
  const response = await fetch(requestUrl, {
    ...requestOptions,
    signal: controller.signal,
  });
  clearTimeout(id);
  return response;
};
